import {Alert} from "react-native";
import ThermalPrinterModule from "react-native-thermal-printer";
import {ColumnAlignment, COMMANDS, NetPrinter} from "react-native-thermal-receipt-printer-image-qr";
import i18n from "../../../i18n";
// TODO: Don't forget to add "new EscPosCharsetEncoding(\"Cp852\", 6)" to "@happy-gastro/react-native-thermal-printer/android"

const getOrderType = (orderType, order = {}) => {
    switch (orderType) {
        case 'online': {
            if (order.orderFrom === 'wolt') return 'Wolt';
            if (order.orderFrom === 'foodpanda') return 'FoodPanda';
            if (order.orderFrom === 'foodPanda') return 'FoodPanda';
            if (order.orderFrom === 'netpincer') return 'FoodPanda';
            if (order.orderFrom === 'falatozz') return 'Falatozz.hu';
            if (order.orderFrom === 'ordit') return 'Ordit.hu';
            if (order.orderFrom === 'webshop') return 'Webshop';
            if (order.orderFrom === 'phone') return 'Phone';
            break;
        }
        case 'dineIn':
            return 'A:' + order.tableName;
        case 'takeAway':
            return 'Elvitel';
        default:
            return orderType || 'Rendelés';
    }
};

const getOrderFrom = (orderFrom) => {
    if (orderFrom === 'wolt') return 'Wolt';
    if (orderFrom === 'foodpanda') return 'FoodPanda';
    if (orderFrom === 'foodPanda') return 'FoodPanda';
    if (orderFrom === 'netpincer') return 'FoodPanda';
    if (orderFrom === 'falatozz') return 'Falatozz.hu';
    if (orderFrom === 'ordit') return 'Ordit.hu';
    if (orderFrom === 'webshop') return 'Webshop';
    if (orderFrom === 'phone') return 'Phone';
    return 'Mobil POS';
};

const getBluetoothPrinterAddress = (printer = {}) => {
    return printer?.address || printer?.macAddress || printer?._nativeDevice?.address || '';
};

export const hasPrinterConnection = (printer = {}) => {
    return Boolean(printer?.ip || getBluetoothPrinterAddress(printer));
};

export const paymentTypes = {
    cash: "Készpénz",
    creditCard: "Bankkártya",
    temporary: "FIZETETLEN",
    vipBalance: "VIP egyenleg",
    employeeBalance: "Személyzeti",
};

const returnResponse = (cb) => {
    globalThis.setTimeout(() => Alert.alert(
        i18n.t('print_failed_title'),
        i18n.t('print_retry_confirm'),
        [
            {
                text: i18n.t('no'),
                style: "destructive",
            },
            {
                text: i18n.t('yes'),
                style: "default",
                onPress: () => {
                    if (cb) cb();
                },
            },
        ],
        {
            cancelable: true,
        }
    ), 1500);
};

export const showPrintErrorDialog = (data, printer, tryAgain = true, showAlert = true) => {
    globalThis.setTimeout(() => Alert.alert(
        i18n.t('print_failed_title'),
        "",
        [
            {
                text: i18n.t('close'),
                style: "cancel",
            },
            {
                text: i18n.t('retry'),
                style: "default",
                onPress: () => {
                    sendPrinterAndroid(data, printer, tryAgain, showAlert);
                },
            },
        ],
        {
            cancelable: true,
        }
    ), 500);
};

export const getPrinterTestData = (printerData) => {
    return (
        "\n\n" +
        "[C]<font size='normal'>Nyomtató észlelve!</font>\n" +
        "[C]============================\n" +
        "[L]" +
        (printerData?.ip || '') +
        "[R]Név: \n" +
        "[L]Port: " +
        (printerData?.port || '') +
        "[R]Típus: \n" +
        "[C]\n\n\n\n\n\n\n\n\n\n\n[C]"
    );
};

// TCP printers sometimes need a moment to accept the first connection
// after the device wakes up Wi‑Fi. The previous 200 ms timeout was far
// too short and caused the very common "didn't print on first try"
// reports. 8 s is a safe default; can be overridden per-printer via
// `printer.timeoutMs`.
const DEFAULT_TCP_TIMEOUT_MS = 8000;
const PRINTER_RETRY_ATTEMPTS = 3;
const PRINTER_RETRY_INITIAL_DELAY_MS = 400;
const PRINTER_RETRY_MAX_DELAY_MS = 2000;

const sleep = (ms) => new Promise(resolve => globalThis.setTimeout(resolve, ms));

const computeBackoffMs = (attempt) => {
    return Math.min(
        PRINTER_RETRY_MAX_DELAY_MS,
        PRINTER_RETRY_INITIAL_DELAY_MS * (2 ** Math.max(0, attempt - 1))
    );
};

/**
 * True when another retry (after `backoffMs`) would start past `deadlineAt`.
 *
 * @param {number|undefined} deadlineAt Epoch ms after which no new attempt may start.
 * @param {number} backoffMs Delay before the next attempt.
 * @returns {boolean}
 */
export const isPastPrintDeadline = (deadlineAt, backoffMs = 0) => {
    return Number.isFinite(deadlineAt) && Date.now() + backoffMs >= deadlineAt;
};

/**
 * Print an ESC/POS payload over TCP or Bluetooth with bounded retries.
 *
 * @param {string} data Payload.
 * @param {object} printer Target (`ip`/`port` or Bluetooth address).
 * @param {boolean} [tryAgain=true] Retry up to PRINTER_RETRY_ATTEMPTS times.
 * @param {boolean} [showAlert=true] Show the retry dialog on final failure.
 * @param {{deadlineAt?: number}} [options] `deadlineAt` (epoch ms): no new
 *   attempt starts after it. Callers that race this call against a timeout pass
 *   it so an abandoned job stops retrying instead of competing with the next
 *   print job for the same printer.
 * @returns {Promise<true|{error: Error, data: string, printer: object}>}
 */
export const sendPrinterAndroid = async (
    data,
    printer,
    tryAgain = true,
    showAlert = true,
    options = {}
) => {
    const deadlineAt = Number(options?.deadlineAt);
    if (!hasPrinterConnection(printer)) {
        if (showAlert) {
            Alert.alert(
                i18n.t('no_printer_selected_title'),
                i18n.t('no_printer_selected_message')
            );
        }
        return {error: new Error('No printer configured'), data, printer};
    }

    const totalAttempts = tryAgain ? PRINTER_RETRY_ATTEMPTS : 1;
    let lastError = null;

    if (printer?.ip) {
        const tcpTimeoutMs = Number(printer?.timeoutMs) || DEFAULT_TCP_TIMEOUT_MS;

        for (let attempt = 1; attempt <= totalAttempts; attempt += 1) {
            try {
                await ThermalPrinterModule.printTcp({
                    ...ThermalPrinterModule.defaultConfig,
                    timeout: tcpTimeoutMs,
                    ...printer,
                    payload: data,
                });
                console.log(`Data printed by TCP/IP! (attempt ${attempt})`);
                return true;
            } catch (error) {
                lastError = error;
                console.warn(`[printer] TCP attempt ${attempt}/${totalAttempts} failed:`, error?.message || error);
                if (attempt < totalAttempts) {
                    if (isPastPrintDeadline(deadlineAt, computeBackoffMs(attempt))) {
                        break;
                    }
                    await sleep(computeBackoffMs(attempt));
                }
            }
        }

        if (showAlert) {
            showPrintErrorDialog(data, printer, tryAgain, showAlert);
        }
        return {error: lastError, data, printer};
    }

    const bluetoothAddress = getBluetoothPrinterAddress(printer);
    if (bluetoothAddress) {
        for (let attempt = 1; attempt <= totalAttempts; attempt += 1) {
            try {
                await ThermalPrinterModule.printBluetooth({
                    ...ThermalPrinterModule.defaultConfig,
                    ...printer,
                    macAddress: bluetoothAddress,
                    payload: data,
                });
                console.log(`Data printed by Bluetooth! (attempt ${attempt})`);
                return true;
            } catch (error) {
                lastError = error;
                console.warn(`[printer] BT attempt ${attempt}/${totalAttempts} failed:`, error?.message || error);
                if (attempt < totalAttempts) {
                    if (isPastPrintDeadline(deadlineAt, computeBackoffMs(attempt))) {
                        break;
                    }
                    await sleep(computeBackoffMs(attempt));
                }
            }
        }

        if (showAlert) {
            showPrintErrorDialog(data, printer, tryAgain, showAlert);
        }
        return {error: lastError, data, printer};
    }

    return {error: new Error('Unsupported printer configuration'), data, printer};
};

export const sendPrinteriOS = async (
    printingOld,
    order,
    restaurant,
    restaurantPlace,
    user,
    printer,
    tryAgain = true,
    showAlert = true
) => {
    if (printer?.ip) {
        try {
            await Promise.all(Array.from({length: printer?.printerEvent?.copies || printer?.printerEvent?.copy || 1}).map(async () => {
                await NetPrinter.connectPrinter(
                    printer?.ip || "",
                    printer?.port || 9100
                );

                const Printer = NetPrinter;
                return new Promise(async (resolve) => {
                    try {
                        Printer.printText(`${COMMANDS.TEXT_FORMAT.TXT_ALIGN_CT}${COMMANDS.TEXT_FORMAT.TXT_BOLD_ON}${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(2, 2)}${("#" + (order.orderCode || "123-456"))}${COMMANDS.TEXT_FORMAT.TXT_BOLD_OFF}`);
                        Printer.printText(`${COMMANDS.TEXT_FORMAT.TXT_ALIGN_CT}${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(2, 2)}${getOrderType(order.orderType || 'takeAway', order)}`);

                        if (order.orderType === 'online') {
                            Printer.printText(`${COMMANDS.TEXT_FORMAT.TXT_ALIGN_CT}${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(1, 1)}${(order.addressInfo.zip + " " + order.addressInfo.city)}\n`);
                            Printer.printText(`${COMMANDS.TEXT_FORMAT.TXT_ALIGN_CT}${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(1, 1)}${(order.addressInfo.address)}\n`);
                        }

                        if (order.comments && order.comments.length > 0) {
                            Printer.printText(`${COMMANDS.TEXT_FORMAT.TXT_ALIGN_CT}"${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(1, 1)}${COMMANDS.TEXT_FORMAT.TXT_BOLD_ON}${(order.comments.join(', '))}${COMMANDS.TEXT_FORMAT.TXT_BOLD_OFF}"\n`);
                        }
                        if (order.comment) {
                            Printer.printText(`${COMMANDS.TEXT_FORMAT.TXT_ALIGN_CT}"${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(1, 1)}${COMMANDS.TEXT_FORMAT.TXT_BOLD_ON}${(order.comment)}${COMMANDS.TEXT_FORMAT.TXT_BOLD_OFF}"\n`);
                        }

                        const generateItemRow = (row) => {
                            let currentSize = row.item.sizes.find((_) => _._id === row.size);
                            if (!currentSize) currentSize = row.item.sizes[row.sizeIndex] || row.item.sizes[0];
                            const itemName = (row.item.name['hu'] || row.item.name['en']) + ((currentSize && currentSize.amount !== 1) ? " (" + currentSize.amount + "" + currentSize.unit + ")" : "");

                            Printer.printText(`${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(2, 2)}${COMMANDS.TEXT_FORMAT.TXT_ALIGN_CT}${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(1, 2)}${COMMANDS.TEXT_FORMAT.TXT_ALIGN_LT}${(row.amount || row.quantity || 0) + "x"}${itemName}`);

                            if (row.comment) {
                                Printer.printText(`${COMMANDS.TEXT_FORMAT.TXT_ALIGN_CT}${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(1, 1)}${'            ' + '"' + row.comment + '"'}`);
                            }
                            if (row.serveType) {
                                Printer.printText(`${COMMANDS.TEXT_FORMAT.TXT_ALIGN_CT}${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(1, 1)}${'            ' + '"' + row.serveType + '"'}`);
                            }
                            if (row.comments && row.comments.length > 0) {
                                Printer.printText(`${COMMANDS.TEXT_FORMAT.TXT_ALIGN_CT}${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(1, 1)}${'            ' + '"' + row.comments.join(', ') + '"'}`);
                            }
                            if (row.extras) {
                                row.extras.forEach((extra) => {
                                    Printer.printText(`${COMMANDS.TEXT_FORMAT.TXT_ALIGN_CT}${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(1, 1)}${'  +' + (extra.item.name.hu || extra.item.name.en)}`);
                                });
                            }
                        };

                        const generateItems = () => [...Array(21).keys()].map((key) => {
                            if ([...order.items].filter((item) => item.round === key).length === 0) return false;
                            if (key > 0) {
                                Printer.printText(`${COMMANDS.TEXT_FORMAT.TXT_ALIGN_CT}${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(1, 2)}${COMMANDS.TEXT_FORMAT.TXT_BOLD_ON}${(key + '. fogás')}${COMMANDS.TEXT_FORMAT.TXT_BOLD_OFF}`);
                            }
                            order.items.filter((item) => item.round === key).map((currentItem) => generateItemRow(currentItem));
                            return true;
                        });

                        generateItems();

                        Printer.printText(`${COMMANDS.TEXT_FORMAT.TXT_ALIGN_CT}${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(1, 1)}${COMMANDS.TEXT_FORMAT.TXT_BOLD_OFF}${COMMANDS.HORIZONTAL_LINE.HR_80MM}`);

                        Printer.printColumnsText([
                            getOrderFrom(order.orderFrom),
                            new Date().toLocaleTimeString('hu-HU'),
                        ], [23, 23], [ColumnAlignment.LEFT, ColumnAlignment.RIGHT], [
                            `${COMMANDS.TEXT_FORMAT.TXT_BOLD_OFF}`,
                            `${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(1, 1)}`,
                            ``
                        ]);

                        Printer.printColumnsText([
                            (printer.name || 'Terminál'),
                            new Date().toLocaleDateString('hu-HU'),
                        ], [23, 23], [ColumnAlignment.LEFT, ColumnAlignment.RIGHT], [
                            `${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(1, 1)}`,
                            `${COMMANDS.TEXT_FORMAT.TXT_CUSTOM_SIZE(1, 1)}`,
                            ``
                        ]);
                        Printer.printBill(``, {beep: false});
                        resolve(true);
                    } catch (error) {
                        console.error(error);
                        resolve(false);
                    }
                });
            }));
            return true;
        } catch (_error) {
            if (showAlert) {
                returnResponse(() => sendPrinteriOS(
                    printingOld,
                    order,
                    restaurant,
                    restaurantPlace,
                    user,
                    printer,
                    tryAgain,
                    showAlert
                ));
            }
            return false;
        }
    }

    if (getBluetoothPrinterAddress(printer)) {
        return false;
    }

    return false;
};
